import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactSegmented, {
  SegmentedOption,
} from 'shared/design-system/components/TrueImpactSegmented';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/pro-light-svg-icons';
import {
  Feature,
  FeaturePlatformOverride,
  PlatformCode,
  deleteFeaturePlatform,
  getFeaturePlatformOverrides,
  setFeaturePlatform,
} from './api';

interface PlatformMeta {
  code: PlatformCode;
  label: string;
  description: string;
}

const PLATFORMS: PlatformMeta[] = [
  { code: 'ti', label: 'True Impact', description: 'TI' },
  { code: 'cn', label: 'Charity Navigator', description: 'CN' },
  { code: 'bb', label: 'Blackbaud', description: 'BB' },
];

type Choice = 'default' | 'on' | 'off';

const Section = styled.div`
  margin-top: ${SPACERS.medium};
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 10px;
  padding: ${SPACERS.small} ${SPACERS.medium} ${SPACERS.medium};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.tiny};
  color: ${COLORS.grayDarkest};
  font-weight: 600;
  margin-bottom: 2px;
`;

const SectionSubtitle = styled.div`
  color: ${COLORS.grayDark};
  font-size: 12px;
  margin-bottom: ${SPACERS.small};
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

const PlatformRow = styled.div<{ isOverridden: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  padding: ${SPACERS.small};
  border: 1px solid
    ${({ isOverridden }) =>
      isOverridden ? COLORS.brandPrimarySubdued : COLORS.greyLight};
  border-radius: 10px;
  background: ${COLORS.whiteBase};
  & + & {
    margin-top: ${SPACERS.tiny};
  }
  .info {
    flex: 1;
    min-width: 0;
    .name {
      font-weight: 600;
      color: ${COLORS.blackBase};
      display: flex;
      align-items: center;
      gap: ${SPACERS.tiny};
    }
    .code-pill {
      display: inline-block;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.4px;
      padding: 2px 6px;
      border-radius: 4px;
      background: ${COLORS.grayLight};
      color: ${COLORS.grayDarkest};
    }
    .status {
      font-size: 12px;
      color: ${COLORS.grayDark};
      margin-top: 2px;
    }
    .status .strong {
      color: ${COLORS.ctaBrand};
      font-weight: 600;
    }
  }
`;

interface PlatformOverridesProps {
  feature: Feature;
}

const PlatformOverrides: React.FC<PlatformOverridesProps> = ({ feature }) => {
  const client = useClient();
  const [overrides, setOverrides] = useState<FeaturePlatformOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyPlatform, setBusyPlatform] = useState<PlatformCode | null>(null);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await getFeaturePlatformOverrides(client, feature.dbId);
      setOverrides(data?.featurePlatformOverrides || []);
    } finally {
      setLoading(false);
    }
  }, [client, feature.dbId]);

  useEffect(() => {
    load();
  }, [load]);

  const overrideByPlatform = useMemo(() => {
    const map = new Map<PlatformCode, FeaturePlatformOverride>();
    overrides.forEach(o => map.set(o.platform, o));
    return map;
  }, [overrides]);

  const applyChoice = async (platform: PlatformCode, choice: Choice) => {
    if (!client) return;
    const existing = overrideByPlatform.get(platform);
    setBusyPlatform(platform);
    try {
      if (choice === 'default') {
        if (existing) await deleteFeaturePlatform(client, existing.dbId);
      } else {
        await setFeaturePlatform(client, {
          featureId: feature.dbId,
          platform,
          value: choice === 'on',
        });
      }
      await load();
    } finally {
      setBusyPlatform(null);
    }
  };

  return (
    <Section>
      <SectionHeader>
        <FontAwesomeIcon icon={faLayerGroup} />
        <TrueImpactText variant="body-bold">Platform overrides</TrueImpactText>
      </SectionHeader>
      <SectionSubtitle>
        Choose the value each platform should see. <b>Default</b> means the
        platform inherits the flag&apos;s default value above. Resolution order:{' '}
        <b>per-user email override</b> → <b>platform override</b> →{' '}
        <b>default</b>.
      </SectionSubtitle>

      {loading && overrides.length === 0 ? (
        <LoadingWrapper>
          <TrueImpactLoading />
        </LoadingWrapper>
      ) : (
        <div>
          {PLATFORMS.map(platform => {
            const existing = overrideByPlatform.get(platform.code);
            const current: Choice = existing
              ? existing.value
                ? 'on'
                : 'off'
              : 'default';
            const busy = busyPlatform === platform.code;
            const defaultLabel = feature.value ? 'On' : 'Off';

            return (
              <PlatformRow
                key={platform.code}
                isOverridden={current !== 'default'}
              >
                <div className="info">
                  <div className="name">
                    {platform.label}
                    <span className="code-pill">{platform.description}</span>
                  </div>
                  <div className="status">
                    {current === 'default' ? (
                      <>
                        Using default (<span>{defaultLabel}</span>)
                      </>
                    ) : (
                      <>
                        Overridden to{' '}
                        <span
                          className="strong"
                          style={{
                            color:
                              current === 'on'
                                ? COLORS.success
                                : COLORS.critical,
                          }}
                        >
                          {current === 'on' ? 'On' : 'Off'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <TrueImpactSegmented<Choice>
                  ariaLabel={`${platform.label} value`}
                  value={current}
                  disabled={busy}
                  onChange={choice => applyChoice(platform.code, choice)}
                  options={
                    [
                      {
                        id: 'default',
                        label: 'Default',
                        selectedColor: COLORS.grayLight,
                        selectedTextColor: COLORS.grayDarkest,
                        title: 'Inherit the flag default',
                      },
                      {
                        id: 'on',
                        label: 'On',
                        selectedColor: COLORS.successBackground,
                        selectedTextColor: COLORS.success,
                        title: `Force ON for ${platform.label}`,
                      },
                      {
                        id: 'off',
                        label: 'Off',
                        selectedColor: COLORS.criticalBackground,
                        selectedTextColor: COLORS.critical,
                        title: `Force OFF for ${platform.label}`,
                      },
                    ] as SegmentedOption<Choice>[]
                  }
                />
              </PlatformRow>
            );
          })}
        </div>
      )}
    </Section>
  );
};

export default PlatformOverrides;
